import { serverNow } from '@/libs/serverTime'
import { getReadableColor } from '@/utils/color'

export const ELAPSED_DEFAULTS = { warning: 90, danger: 180 };

// Etapas que têm limites de alerta/perigo configurados (Tempos de alerta do nó). As demais
// (ex.: "liberado") não têm limite próprio, então nunca entram em perigo.
export const ALERT_STEPS = ['solicita', 'regulacao', 'despacha'];

export function formatClock(totalSeconds) {
  const diff = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;

  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function isValidPair(pair) {
  return !!pair && Number.isFinite(pair.warning) && Number.isFinite(pair.danger);
}

/**
 * Limites warning/danger de uma etapa a partir de state.step_alerts.
 * Sem disciplina, usa o par mais rígido entre as disciplinas do nó.
 */
export function resolveStepThresholds(map, stepKey, disciplineId = null) {
  if (!stepKey) return ELAPSED_DEFAULTS;

  if (disciplineId != null) {
    const pair = (map[String(disciplineId)] || {})[stepKey];
    return isValidPair(pair) ? pair : ELAPSED_DEFAULTS;
  }

  const pairs = Object.keys(map)
    .map((id) => (map[id] || {})[stepKey])
    .filter(isValidPair);

  if (!pairs.length) return ELAPSED_DEFAULTS;

  return pairs.reduce((a, b) => {
    if (b.warning !== a.warning) return b.warning < a.warning ? b : a;
    return b.danger < a.danger ? b : a;
  });
}

export default {
  data() {
    return {
      elapsedNow: serverNow(),
      elapsedTimer: null,
    };
  },

  mounted() {
    this.elapsedTimer = setInterval(() => {
      // Quem usa o mixin pode declarar `elapsedStopped` (ex.: ocorrência finalizada): o relógio para.
      if (this.elapsedStopped) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;

        return;
      }
      this.elapsedNow = serverNow();
    }, 1000);
  },

  beforeDestroy() {
    if (this.elapsedTimer) {
      clearInterval(this.elapsedTimer);
    }
  },

  methods: {
    // `endStr` (opcional): data em que a contagem parou, ex.: a finalização da ocorrência.
    elapsedHHMMSS(dateStr, endStr = null) {
      if (!dateStr) return '--:--:--';
      const now = endStr ? new Date(endStr).getTime() : this.elapsedNow;

      return formatClock((now - new Date(dateStr).getTime()) / 1000);
    },

    // Tempo-resposta da ocorrência: da abertura até a 1ª equipe chegar ao local (a posição com a flag
    // "marca chegada" grava `arrived_at`). Corre (piscando) enquanto ninguém chegou; depois fica fixo.
    // `text` vem nulo quando a ocorrência terminou sem equipe ter chegado (ex.: informação).
    responseTimeOf(occ) {
      if (occ.arrived_at) return { text: this.elapsedHHMMSS(occ.created_at, occ.arrived_at), running: false };
      if (occ.finalized_at) return { text: null, running: false };

      return { text: this.elapsedHHMMSS(occ.created_at), running: true };
    },

    // Selo na cor da classificação da ocorrência (texto legível sobre o fundo claro).
    responseBadgeStyle(occ) {
      const hex = (occ.classification && occ.classification.hex_color) || '#7367f0';

      return { color: getReadableColor(hex), backgroundColor: `${hex}22`, border: `1px solid ${hex}88` };
    },

    // Quanto passou do tempo de perigo da etapa ("HH:MM:SS"), ou null enquanto não passou
    // (e para etapas sem limite configurado).
    dangerOverHHMMSS(dateStr, stepKey, disciplineId = null) {
      if (!dateStr || !ALERT_STEPS.includes(stepKey)) return null;

      const { danger } = this.elapsedThresholds(stepKey, disciplineId);
      const over = (this.elapsedNow - new Date(dateStr).getTime()) / 1000 - danger;

      return over > 0 ? formatClock(over) : null;
    },

    elapsedBadgeClass(dateStr, stepKey = null, disciplineId = null) {
      if (!dateStr) return 'badge elapsed-badge elapsed-badge--ok';

      const { warning, danger } = this.elapsedThresholds(stepKey, disciplineId);
      const now = this.elapsedNow;
      const seconds = Math.floor((now - new Date(dateStr).getTime()) / 1000);

      if (seconds < warning) return 'badge elapsed-badge elapsed-badge--ok';
      if (seconds < danger) return 'badge elapsed-badge elapsed-badge--warning';
      return 'badge elapsed-badge elapsed-badge--danger';
    },

    elapsedThresholds(stepKey, disciplineId) {
      return resolveStepThresholds((this.$store && this.$store.state.step_alerts) || {}, stepKey, disciplineId);
    },
  },
};
